/**
 * Net Profit Bonus — "reach this net profit, earn this; reach a higher one,
 * earn more."
 *
 * The same idea as the step bonus on a Build Your Own card (see
 * components/CustomWidget/bonusSteps.js), aimed at the one figure that card can
 * not price: Net profit, which the Revenue, Expenses & Labor card computes as
 * Revenue − Labor − Expenses and reads two ways — in dollars, and as a share of
 * revenue (margin). A ladder can be run on either, or on both at once:
 *
 *   amount   a step is reached when net profit hits `at` dollars
 *   share    a step is reached when the margin hits `atPct` percent
 *   both     a step is reached only when BOTH lines are met
 *
 * "Both" is the one worth having. Dollars alone can be hit by pushing revenue at
 * a terrible margin; a margin alone can be hit in a month too small to matter.
 * Asking for both closes each loophole with the other.
 *
 *   { mode, per: "team" | "player", label,
 *     steps: [{ at, atPct, pay, payUnit? }, …] }   (max 4; `at` / `atPct` per mode)
 *
 * What a step PAYS is either a flat dollar amount (the default) or a percentage
 * of the period's total revenue — `payUnit: "%"`, stored only when it is the
 * non-default, so a ladder saved before percent pay existed reads unchanged. A
 * percent step has no fixed dollar figure: it is worked out from the revenue the
 * profit card reports (payAmount below), so it moves with the period.
 *
 * Everything here is pure — the Setup preview, the card and the layout store all
 * go through the same sanitizer.
 */

import { matchesWidgetRef } from "../../helpers/widgetRef";

export const MAX_NET_STEPS = 4;
export const NET_MODES = [
  { id: "amount", label: "Dollars", sub: "Net profit in dollars" },
  { id: "share", label: "Percent", sub: "Net profit as a share of revenue" },
  { id: "both", label: "Both", sub: "Both lines must be met" },
];
export const NET_PER = ["team", "player"];
export const DEFAULT_NET_LABEL = "Net profit bonus";

const num = (v) => {
  if (v === "" || v == null) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};
const round2 = (n) => Math.round(n * 100) / 100;

export const netModeOf = (b) => (b && (b.mode === "share" || b.mode === "both") ? b.mode : "amount");
export const usesAmount = (mode) => mode !== "share";
export const usesShare = (mode) => mode !== "amount";

/* -- What a step pays ------------------------------------------------------ */

export const PAY_UNITS = [
  { id: "$", label: "Dollars" },
  { id: "%", label: "% of revenue" },
];
export const payUnitOf = (step) => (step && step.payUnit === "%" ? "%" : "$");

/**
 * What one step comes to in dollars. A flat step is its own figure; a percent
 * step is that share of the period's total revenue — the same revenue the profit
 * card's tile reports — so with no revenue it is nothing rather than a guess.
 */
export const payAmount = (step, revenue) => {
  if (!step) return 0;
  if (payUnitOf(step) !== "%") return Number(step.pay) || 0;
  const rev = Number(revenue);
  return Number.isFinite(rev) && rev > 0 ? round2((rev * (Number(step.pay) || 0)) / 100) : 0;
};

const trimNum = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const dollars = (n) => `$${trimNum(Math.round(Number(n) || 0))}`;

/** What the step is written as, split so a card can set the figure big and the
    words small: { value: "1%", unit: "of revenue" } or { value: "$250", unit: "" }. */
export const payParts = (step, long = false) => (
  payUnitOf(step) === "%"
    ? { value: `${trimNum(step.pay)}%`, unit: `of ${long ? "total " : ""}revenue` }
    : { value: dollars(step.pay), unit: "" }
);

/** "$250" or "1% of revenue". */
export const payText = (step, long = false) => {
  const { value, unit } = payParts(step, long);
  return unit ? `${value} ${unit}` : value;
};

/**
 * One step as a sentence, in the words the admin would say it out loud:
 *
 *   At $20,000 net profit & 10% net margin pays 1% of total revenue
 *
 * Setup prints these above the inputs so the ladder can be read, not decoded.
 */
export const netStepSentence = (step, mode) => {
  const parts = [];
  if (usesAmount(mode)) parts.push(`${dollars(step.at)} net profit`);
  if (usesShare(mode)) parts.push(`${trimNum(step.atPct)}% net margin`);
  return `At ${parts.join(" & ")} pays ${payText(step, true)}`;
};

/* -- Which Revenue, Expenses & Labor card feeds this one ------------------- */

/** Every Revenue, Expenses & Labor card on this board, in board order. */
export const profitWidgetsOn = (boardWidgets) => (boardWidgets || []).filter((w) => w && w.comp === "profit");

/**
 * Which profit card this one reads. An explicit pick wins; with none it falls to
 * the only one on the board — but it never guesses between two, because two of
 * them can be set up differently (different expenses, different payroll card)
 * and "whichever came first" would quietly pay a bonus on the wrong Net. Same
 * rule the profit card itself uses to find its payroll card
 * (resolveLaborWidget in components/ProfitTrend/data.js).
 */
export const resolveProfitWidget = (settings, boardWidgets) => {
  const choices = profitWidgetsOn(boardWidgets);
  if (!choices.length) return { widget: null, choices, ambiguous: false, missing: true };
  const wanted = settings?.profitWidgetId ? String(settings.profitWidgetId) : "";
  const picked = wanted ? choices.find((w) => matchesWidgetRef(w, wanted)) : null;
  if (picked) return { widget: picked, choices, ambiguous: false, missing: false };
  /* A pick that no longer resolves is not the same as no pick: the card it named
     was removed, and sliding onto a different one would change the number the
     bonus is paid on without saying so. */
  if (wanted) return { widget: null, choices, ambiguous: true, missing: false, stale: true };
  if (choices.length === 1) return { widget: choices[0], choices, ambiguous: false, missing: false };
  return { widget: null, choices, ambiguous: true, missing: false };
};

/* -- The ladder ------------------------------------------------------------ */

/**
 * Persist shape — drops junk, sorts the ladder, and returns null when nothing is
 * payable, so an unfinished bonus never reaches the board.
 *
 * Each mode keeps only the fields it reads (an `amount` ladder carries no
 * `atPct`), so flipping mode in Setup never leaves a stale line behind on the
 * board. In "both" a step must be harder than the one below it on EVERY line —
 * one that is easier on either would be reached before its predecessor, and
 * "the highest step reached" would stop meaning anything — so it is dropped.
 */
export const sanitizeNetBonus = (b) => {
  if (!b || typeof b !== "object") return null;
  const mode = netModeOf(b);
  const label = typeof b.label === "string" && b.label.trim() ? b.label.trim().slice(0, 60) : DEFAULT_NET_LABEL;
  const needAt = usesAmount(mode);
  const needPct = usesShare(mode);

  let steps = (Array.isArray(b.steps) ? b.steps : [])
    .map((s) => ({
      at: num(s && s.at),
      atPct: num(s && s.atPct),
      pay: num(s && s.pay),
      payUnit: payUnitOf(s),
    }))
    .filter((s) => s.pay > 0 && (!needAt || s.at > 0) && (!needPct || s.atPct > 0))
    .map((s) => ({
      ...(needAt ? { at: round2(s.at) } : null),
      ...(needPct ? { atPct: round2(Math.min(100, s.atPct)) } : null),
      /* More than all of the revenue is not a bonus anyone meant to write. */
      pay: round2(s.payUnit === "%" ? Math.min(100, s.pay) : s.pay),
      ...(s.payUnit === "%" ? { payUnit: "%" } : null),
    }));

  /* Low to high on the line that leads: dollars when they are in play, else the
     margin. */
  const lead = needAt ? "at" : "atPct";
  steps.sort((a, c) => a[lead] - c[lead] || (needPct ? a.atPct - c.atPct : 0));
  /* Two steps on the same line cannot both be "the next one" — keep the first. */
  steps = steps.filter((s, i, arr) => i === 0 || s[lead] !== arr[i - 1][lead]);
  if (mode === "both") {
    steps = steps.reduce((out, s) => {
      if (out.length && s.atPct < out[out.length - 1].atPct) return out;
      out.push(s);
      return out;
    }, []);
  }
  steps = steps.slice(0, MAX_NET_STEPS);
  if (!steps.length) return null;
  return { mode, per: b.per === "player" ? "player" : "team", label, steps };
};

/**
 * What is wrong with the rows as typed, in words — for Setup to say under the
 * ladder instead of the sanitizer silently dropping a step. Null when the rows
 * are fine (empty rows are not an error; they are just not steps yet).
 */
export const netStepIssue = (bonus) => {
  if (!bonus) return null;
  const mode = netModeOf(bonus);
  const rows = (Array.isArray(bonus.steps) ? bonus.steps : [])
    .map((s, i) => ({ i, at: num(s && s.at), atPct: num(s && s.atPct), pay: num(s && s.pay) }))
    .filter((r) => [r.at, r.atPct, r.pay].some((v) => Number.isFinite(v)));
  const needAt = usesAmount(mode);
  const needPct = usesShare(mode);
  const over = (Array.isArray(bonus.steps) ? bonus.steps : [])
    .findIndex((s) => payUnitOf(s) === "%" && num(s && s.pay) > 100);
  if (over >= 0) return `Step ${over + 1} pays more than 100% of revenue — lower it.`;
  const half = rows.find((r) => !(r.pay > 0) || (needAt && !(r.at > 0)) || (needPct && !(r.atPct > 0)));
  if (half) {
    return `Step ${half.i + 1} isn't finished — it needs ${mode === "both" ? "both lines and " : "its line and "}what it pays.`;
  }
  if (mode === "both") {
    const sorted = rows.slice().sort((a, c) => a.at - c.at);
    for (let k = 1; k < sorted.length; k += 1) {
      if (sorted[k].at === sorted[k - 1].at) return "Two steps share the same net profit line — give each its own.";
      if (sorted[k].atPct < sorted[k - 1].atPct) {
        return "A higher step has to be harder on both lines — its margin can't be lower than the step below it.";
      }
    }
  }
  return null;
};

/** A ladder to start from, so switching a mode on shows a real example. */
export const defaultNetSteps = () => [
  { at: 20000, atPct: 10, pay: 250 },
  { at: 35000, atPct: 15, pay: 500 },
];

/**
 * Where the period's figures stand on the ladder.
 *
 *   idx     the highest step reached (-1 = none yet)
 *   pay     what that step pays, in dollars (0 below the first line). A percent
 *           step is worked out from `revenue`, the period's total revenue
 *   next    the next step up, or null at the top
 *   rows    what the next step still asks for, line by line — { key, have, need,
 *           gap, ok } — so the card can say exactly what is holding it back
 *   flags   per step: which of its lines are met, and whether it is reached
 *   max     the most any step pays, in dollars at the current revenue
 *   done    every step reached
 *
 * `share` is null when there is no revenue to measure against; a share line is
 * then simply not met.
 */
export const netBonusState = (bonus, { amount, share, revenue }) => {
  const steps = (bonus && bonus.steps) || [];
  const mode = netModeOf(bonus);
  const a = Number(amount) || 0;
  const s = share == null || !Number.isFinite(Number(share)) ? null : Number(share);

  const flags = steps.map((st) => {
    const amountMet = usesAmount(mode) ? a >= st.at : null;
    const shareMet = usesShare(mode) ? s != null && s >= st.atPct : null;
    return { amountMet, shareMet, hit: amountMet !== false && shareMet !== false };
  });
  let idx = -1;
  flags.forEach((f, i) => { if (f.hit) idx = i; });
  const next = steps[idx + 1] || null;

  const rows = [];
  if (next) {
    if (usesAmount(mode)) {
      rows.push({ key: "amount", have: a, need: next.at, gap: round2(Math.max(0, next.at - a)), ok: a >= next.at });
    }
    if (usesShare(mode)) {
      const have = s == null ? 0 : s;
      rows.push({ key: "share", have, need: next.atPct, gap: round2(Math.max(0, next.atPct - have)), ok: s != null && s >= next.atPct });
    }
  }

  return {
    idx,
    pay: idx >= 0 ? payAmount(steps[idx], revenue) : 0,
    next,
    rows,
    flags,
    max: steps.length ? Math.max(...steps.map((x) => payAmount(x, revenue))) : 0,
    done: steps.length > 0 && idx === steps.length - 1,
  };
};

/**
 * Where the value and every step sit on the progress rail (0–100).
 *
 * Steps are spaced evenly, and the value is placed BETWEEN the two steps it is
 * standing between — not on a dollar or percent scale. A $20k line and a 10%
 * line share no scale, and on "both" the rail has to answer to whichever one is
 * further behind, so the position is worked out per line and the smaller wins:
 * the bar never shows more progress than the payout justifies.
 */
export const netRail = (bonus, { amount, share }) => {
  const steps = (bonus && bonus.steps) || [];
  const mode = netModeOf(bonus);
  const n = steps.length;
  if (!n) return { valueX: 0, marks: [] };
  const xs = steps.map((_, i) => ((i + 1) / n) * 100);

  const posOf = (v, lines) => {
    if (!Number.isFinite(v) || v <= 0) return 0;
    if (v < lines[0]) return (v / lines[0]) * xs[0];
    for (let i = 0; i < n - 1; i += 1) {
      if (v < lines[i + 1]) return xs[i] + ((v - lines[i]) / (lines[i + 1] - lines[i])) * (xs[i + 1] - xs[i]);
    }
    return 100;
  };

  const positions = [];
  if (usesAmount(mode)) positions.push(posOf(Number(amount), steps.map((x) => x.at)));
  if (usesShare(mode)) {
    positions.push(share == null ? 0 : posOf(Number(share), steps.map((x) => x.atPct)));
  }
  return { valueX: Math.min(...positions), marks: xs };
};

/** 0–100 for a requirement's mini bar. */
export const reqFill = (have, need) => {
  if (!(need > 0)) return 100;
  return Math.max(0, Math.min(100, (Number(have) / need) * 100));
};

/** What a freshly added card starts with — an example ladder, so it shows a real
    bonus straight away instead of an empty box. Setup says they are examples. */
export const defaultNetBonus = () => ({
  mode: "amount",
  per: "team",
  label: DEFAULT_NET_LABEL,
  steps: defaultNetSteps(),
});
